import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { GPXData, AidStation, RouteSegment } from '../types';
import { parseGPX } from '../utils/gpxParser';
import { calculateSegments } from '../utils/routeUtils';
import { augmentGpxElevation, OpenTopodataProvider, AugmentationOptions } from '../gpx-elevation';

interface SidebarProps {
  gpxData: GPXData | null;
  setGpxData: (data: GPXData | null) => void;
  rawGpxString: string | null;
  setRawGpxString: (gpxString: string | null) => void;
  aidStations: AidStation[];
  setAidStations: (stations: AidStation[]) => void;
  segments: RouteSegment[];
  setSegments: (segments: RouteSegment[]) => void;
  isElevationPanelOpen: boolean;
  toggleElevationPanel: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  gpxData,
  setGpxData,
  rawGpxString,
  setRawGpxString,
  aidStations,
  setAidStations,
  segments,
  setSegments,
  isElevationPanelOpen,
  toggleElevationPanel,
}) => {
  const [newStation, setNewStation] = useState({ name: '', distance: '' });
  const [editingSegment, setEditingSegment] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [isAugmenting, setIsAugmenting] = useState(false);
  const [augmentError, setAugmentError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const gpxString = event.target?.result as string;
        if (gpxString) {
            setRawGpxString(gpxString);
            setAugmentError(null);
            try {
                const parsedData = parseGPX(gpxString);
                setGpxData(parsedData);
                setAidStations([]);
                setSegments([]);
            } catch (error) {
                console.error("Error parsing GPX file:", error);
                setGpxData(null);
                setRawGpxString(null);
                setAugmentError(`Error parsing GPX: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        } else {
            setAugmentError('Failed to read file content.');
        }
      };
      reader.onerror = () => {
          console.error("Error reading file");
          setGpxData(null);
          setRawGpxString(null);
          setAugmentError('Error reading the selected file.');
      }
      reader.readAsText(file);
    }
  }, [setGpxData, setRawGpxString, setAidStations, setSegments]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/gpx+xml': ['.gpx'],
    },
  });

  const handleAugmentElevation = async () => {
      if (!rawGpxString) {
          setAugmentError('No GPX file loaded to augment.');
          return;
      }
      
      setIsAugmenting(true);
      setAugmentError(null);
      console.log('Starting elevation augmentation via UI...');

      try {
          const provider = new OpenTopodataProvider();
          const options: AugmentationOptions = {
              resampleIntervalMeters: 50,
              smoothingWindow: 5,
          };

          const augmentedGpxString = await augmentGpxElevation(rawGpxString, provider, options);
          
          setRawGpxString(augmentedGpxString); 
          
          const newGpxData = parseGPX(augmentedGpxString);
          setGpxData(newGpxData);
          
          console.log('Elevation augmentation successful!');

      } catch (error) {
          console.error('Elevation augmentation failed:', error);
          setAugmentError(`Augmentation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
          setIsAugmenting(false);
      }
  };

  const handleAddStation = (e: React.FormEvent) => {
    e.preventDefault();
    if (newStation.name && newStation.distance) {
      const station: AidStation = {
        id: Date.now().toString(),
        name: newStation.name,
        distance: parseFloat(newStation.distance),
        pcNumber: 0  // Use 0 for manually added stations
      };
      const newStations = [...aidStations, station];
      setAidStations(newStations);
      
      if (gpxData) {
        const newSegments = calculateSegments(gpxData, newStations);
        setSegments(newSegments);
      }
      
      setNewStation({ name: '', distance: '' });
    }
  };

  const handleRemoveStation = (id: string) => {
    const newStations = aidStations.filter(station => station.id !== id);
    setAidStations(newStations);
    
    if (gpxData) {
      const newSegments = calculateSegments(gpxData, newStations);
      setSegments(newSegments);
    }
  };

  const handleEditSegment = (segment: RouteSegment) => {
    setEditingSegment(segment.id);
    setEditName(segment.name);
  };

  const handleSaveSegmentName = (segmentId: string) => {
    setSegments(segments.map(segment => 
      segment.id === segmentId 
        ? { ...segment, name: editName || segment.name }
        : segment
    ));
    setEditingSegment(null);
    setEditName('');
  };

  const handleDownloadSegment = (segment: RouteSegment) => {
    if (!gpxData) return;

    // Create a new GPX document
    const doc = document.implementation.createDocument(null, 'gpx', null);
    const gpx = doc.documentElement;
    gpx.setAttribute('version', '1.1');
    gpx.setAttribute('creator', 'Route Editor');
    gpx.setAttribute('xmlns', 'http://www.topografix.com/GPX/1/1');

    // Add metadata
    const metadata = doc.createElement('metadata');
    const name = doc.createElement('name');
    name.textContent = segment.name;
    metadata.appendChild(name);
    gpx.appendChild(metadata);

    // Add track
    const trk = doc.createElement('trk');
    const trkName = doc.createElement('name');
    trkName.textContent = segment.name;
    trk.appendChild(trkName);

    const trkseg = doc.createElement('trkseg');
    segment.coordinates.forEach(coord => {
      const trkpt = doc.createElement('trkpt');
      trkpt.setAttribute('lat', coord[1].toString());
      trkpt.setAttribute('lon', coord[0].toString());
      // Add elevation if available
      if (coord.length > 2 && coord[2] !== undefined) {
          const ele = doc.createElement('ele');
          ele.textContent = coord[2].toString();
          trkpt.appendChild(ele);
      }
      trkseg.appendChild(trkpt);
    });
    trk.appendChild(trkseg);
    gpx.appendChild(trk);

    // Convert to string
    const serializer = new XMLSerializer();
    const gpxString = serializer.serializeToString(doc);

    // Create and trigger download
    const blob = new Blob([gpxString], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${segment.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.gpx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h2>Route Editor</h2>
      </div>
      <div className="sidebar-content">
        <div
          {...getRootProps()}
          className={`drop-zone ${isDragActive ? 'drag-active' : ''}`}
        >
          <input {...getInputProps()} />
          <p>Drag & drop a GPX file here, or click to select</p>
          {gpxData && (
            <p style={{ marginTop: '10px', color: '#4CAF50' }}>
              File loaded: {gpxData.metadata.name || 'Unnamed route'}
            </p>
          )}
        </div>

        <div className="elevation-augmentation">
          <h3>Elevation Tools</h3>
          <button 
            onClick={handleAugmentElevation}
            disabled={!rawGpxString || isAugmenting}
          >
            {isAugmenting ? 'Augmenting...' : 'Augment Elevation (OpenTopoData)'}
          </button>
          {augmentError && <p className="error-message">{augmentError}</p>}
        </div>

        <div className="view-options">
          <h3>View Options</h3>
          <button onClick={toggleElevationPanel} disabled={!gpxData}>
            {isElevationPanelOpen ? 'Hide' : 'Show'} Elevation Profile
          </button>
        </div>

        <form onSubmit={handleAddStation}>
          <h3>Add Aid Station</h3>
          <div className="form-group">
            <label htmlFor="station-name">Station Name</label>
            <input
              id="station-name"
              type="text"
              placeholder="Enter station name"
              value={newStation.name}
              onChange={(e) => setNewStation({ ...newStation, name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="station-distance">Distance (km)</label>
            <input
              id="station-distance"
              type="number"
              placeholder="Enter distance in kilometers"
              value={newStation.distance}
              onChange={(e) => setNewStation({ ...newStation, distance: e.target.value })}
            />
          </div>
          <button type="submit">Add Station</button>
        </form>

        <div className="aid-stations-list">
          <h3>Aid Stations</h3>
          {aidStations.length === 0 ? (
            <p>No aid stations added yet</p>
          ) : (
            [...aidStations]
              .sort((a, b) => a.distance - b.distance)
              .map((station) => (
                <div key={station.id} className="aid-station-item">
                  <div>
                    <strong>{station.name}</strong>
                    <br />
                    <small>{station.distance.toFixed(2)} km</small>
                    {station.pcNumber > 0 && <small> (PC{station.pcNumber})</small>}
                  </div>
                  <button onClick={() => handleRemoveStation(station.id)}>
                    Remove
                  </button>
                </div>
              ))
          )}
        </div>

        <div className="segments-list">
          <h3>Route Segments</h3>
          {segments.length === 0 ? (
            <p>No segments created yet</p>
          ) : (
            segments.map((segment) => (
              <div key={segment.id} className="segment-item">
                {editingSegment === segment.id ? (
                  <div className="segment-edit">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveSegmentName(segment.id);
                        }
                      }}
                      autoFocus
                    />
                    <button onClick={() => handleSaveSegmentName(segment.id)}>
                      Save
                    </button>
                    <button onClick={() => setEditingSegment(null)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="segment-display">
                    <div>
                      <strong>{segment.name}</strong>
                      <br />
                      <small>{segment.distance.toFixed(2)} km</small>
                      <br />
                      <small>{segment.description}</small>
                    </div>
                    <div className="segment-actions">
                      <button onClick={() => handleEditSegment(segment)}>
                        Edit Name
                      </button>
                      <button onClick={() => handleDownloadSegment(segment)} disabled={!gpxData}>
                        Download Segment GPX
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar; 